import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Textarea,
  ToggleButtonGroup,
} from "@mui/joy";
import { HTTPError } from "griptonite-api-client/client";
import { ApiCreditAccountAdmin } from "griptonite-api-client/types";
import { useState } from "react";

import { useCreateCreditAdjustmentMutation, useCreditSystemAccountsQuery } from "@/hooks/credits";
import pluralize from "@/utils/pluralize";

type Direction = "add" | "remove";

// A grant over one of the system's caps, or a removal over the balance, comes back as a 400 whose
// message says how much room or balance is left, which is the useful part to show.
const adjustmentErrorMessage = (error: Error) => {
  if (error instanceof HTTPError && error.response.status === 400) {
    const message = error.jsonData?.detail?.message;
    if (typeof message === "string") {
      return message;
    }
  }
  return "Couldn’t adjust the balance. Please try again.";
};

// Adds credits to a member's account or takes them away. Mounted fresh each time it opens, so it
// starts empty.
export default function AdjustmentModal({
  account,
  systemSlug,
  maxAccountBalance,
  onClose,
}: {
  account: ApiCreditAccountAdmin;
  systemSlug: string;
  /** The most credits one member can hold. Null means no limit. */
  maxAccountBalance: number | null;
  onClose: () => void;
}) {
  const [direction, setDirection] = useState<Direction>("add");
  const [amountText, setAmountText] = useState("");
  const [note, setNote] = useState("");
  const mutation = useCreateCreditAdjustmentMutation(systemSlug, account.uuid);
  const accountsQuery = useCreditSystemAccountsQuery(systemSlug);
  const [isCheckingBalance, setIsCheckingBalance] = useState(false);

  // The API refuses a removal over the balance, so this check only gives instant feedback. The
  // balance follows the accounts list, which refetches while the dialog is open, and is fetched
  // afresh on submit in case it changed since. If it still changes in between, the API's 400 is
  // shown.
  const balanceOf = (accounts?: ApiCreditAccountAdmin[]) =>
    accounts?.find((item) => item.uuid === account.uuid)?.balance ?? account.balance;
  const balance = balanceOf(accountsQuery.data);

  const amount = Number(amountText);
  const isPositiveInteger = Number.isInteger(amount) && amount > 0;
  const exceedsBalance = direction === "remove" && isPositiveInteger && amount > balance;
  // The API also refuses a grant over the system's overall exposure, which only it can check, so
  // that one is still shown from its 400.
  const roomUnderCap =
    maxAccountBalance === null ? Infinity : Math.max(0, maxAccountBalance - balance);
  const exceedsCap = direction === "add" && isPositiveInteger && amount > roomUnderCap;
  const isValid = isPositiveInteger && !exceedsBalance && !exceedsCap;

  const submit = async () => {
    if (direction === "remove") {
      setIsCheckingBalance(true);
      const { data } = await accountsQuery.refetch();
      setIsCheckingBalance(false);
      if (amount > balanceOf(data)) return;
    }
    mutation.mutate(
      {
        amount: direction === "add" ? amount : -amount,
        ...(note.trim() ? { note: note.trim() } : {}),
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Modal open onClose={onClose}>
      <ModalDialog sx={{ width: "100%", maxWidth: 440 }}>
        <ModalClose />
        <DialogTitle>Adjust {account.user.name}’s balance</DialogTitle>
        {/* The theme centres dialog text for confirmations; this is a form. */}
        <DialogContent sx={{ textAlign: "left" }}>
          <Stack
            component="form"
            id="adjustment-form"
            gap={2}
            sx={{ pt: 1 }}
            onSubmit={(event) => {
              event.preventDefault();
              if (isValid) submit();
            }}
          >
            <ToggleButtonGroup
              sx={{ alignSelf: "flex-start" }}
              value={direction}
              onChange={(_, value) => {
                if (value) {
                  mutation.reset();
                  setDirection(value);
                }
              }}
            >
              <Button value="add">Add credits</Button>
              <Button value="remove">Remove credits</Button>
            </ToggleButtonGroup>
            <FormControl error={exceedsBalance || exceedsCap}>
              <FormLabel>Amount</FormLabel>
              <Input
                type="number"
                value={amountText}
                onChange={(event) => {
                  mutation.reset();
                  setAmountText(event.target.value);
                }}
                slotProps={{ input: { min: 1, step: 1 } }}
                endDecorator="credits"
              />
              <FormHelperText>
                {exceedsBalance
                  ? `You can’t remove more than their balance of ${pluralize(balance, "credit")}.`
                  : exceedsCap
                    ? roomUnderCap === 0
                      ? `They’re at the maximum balance of ${pluralize(maxAccountBalance!, "credit")}, so you can’t add more.`
                      : `You can add at most ${pluralize(roomUnderCap, "credit")}, which is as many as fit under the maximum balance of ${maxAccountBalance!.toLocaleString()}.`
                    : `Current balance: ${pluralize(balance, "credit")}.`}
              </FormHelperText>
            </FormControl>
            <FormControl>
              <FormLabel>Note (optional)</FormLabel>
              <Textarea
                minRows={2}
                value={note}
                onChange={(event) => setNote(event.target.value)}
              />
              <FormHelperText>The member sees this in their credit history.</FormHelperText>
            </FormControl>
            {mutation.error ? (
              <Alert color="danger">{adjustmentErrorMessage(mutation.error)}</Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            type="submit"
            form="adjustment-form"
            color={direction === "remove" ? "danger" : "primary"}
            disabled={!isValid}
            loading={mutation.isPending || isCheckingBalance}
          >
            {direction === "add" ? "Add credits" : "Remove credits"}
          </Button>
          <Button variant="plain" color="neutral" onClick={onClose}>
            Cancel
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
}
